import type Phaser from 'phaser';

export interface BackToFloorPillOptions {
  scene: Phaser.Scene;
  x: number;
  y: number;
  /** Scale multiplier for px sizes (default 1). */
  scale?: number;
  onClick: () => void;
  /** Pill label. Defaults to "◀ FLOOR" for play mode. */
  label?: string;
}

export class BackToFloorPill {
  private rect: Phaser.GameObjects.Rectangle;
  private text: Phaser.GameObjects.Text;
  private bg: Phaser.GameObjects.Graphics;
  private originX: number;
  private originY: number;
  private padX: number;
  private padY: number;
  private stripeW: number;
  private currentOnClick: () => void;

  constructor(opts: BackToFloorPillOptions) {
    const { scene, x, y, onClick } = opts;
    const sm = opts.scale ?? 1;
    this.originX = x;
    this.originY = y;
    this.padX = 9 * sm;
    this.padY = 5 * sm;
    this.stripeW = 2 * sm;
    this.currentOnClick = onClick;

    this.text = scene.add
      .text(x + this.padX, y + this.padY, opts.label ?? '◀ FLOOR', {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${Math.round(11 * sm)}px`,
        color: '#d4af37',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);

    this.bg = scene.add.graphics();
    this.rect = scene.add.rectangle(0, 0, 1, 1, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.rect.on('pointerdown', () => this.currentOnClick());
    this.rect.on('pointerover', () => this.text.setColor('#f4d35e'));
    this.rect.on('pointerout', () => this.text.setColor('#d4af37'));

    this.relayout();
  }

  /** Update label + click handler in place (e.g. swap FLOOR → EXIT). */
  setLabel(label: string, onClick: () => void): void {
    this.text.setText(label);
    this.currentOnClick = onClick;
    this.relayout();
  }

  private relayout(): void {
    const w = this.text.width + this.padX * 2;
    const h = this.text.height + this.padY * 2;
    this.bg.clear();
    this.bg.fillStyle(0x06100c, 0.7);
    this.bg.fillRect(this.originX, this.originY, w, h);
    this.bg.fillStyle(0xd4af37, 1);
    this.bg.fillRect(this.originX, this.originY, this.stripeW, h);
    this.bg.setDepth(-1);
    this.rect.setPosition(this.originX + w / 2, this.originY + h / 2);
    this.rect.setSize(w, h);
  }

  destroy() {
    this.rect.destroy();
    this.text.destroy();
    this.bg.destroy();
  }
}
